import { useState } from "react";
import type { SearchQuery } from "../../types/vtc";
import { ALL_AREAS } from "../../data/home";
import { addDaysIso, today } from "../../lib/format";
import { useT } from "../../i18n";
import CityAutocomplete from "../ui/CityAutocomplete";

interface SearchBarProps {
  onSearch: (query: SearchQuery) => void;
}

const fieldWrap =
  "flex min-h-12 flex-col justify-center gap-0.5 rounded bg-surface px-2.5 py-1.5 text-left";
const fieldLabel = "text-[10px] font-medium text-muted";
const fieldInput =
  "w-full border-0 bg-transparent p-0 text-[13px] text-ink outline-none";

export default function SearchBar({ onSearch }: SearchBarProps) {
  const t = useT();
  const [query, setQuery] = useState<SearchQuery>({
    city: ALL_AREAS,
    start: today(),
    end: addDaysIso(today(), 1), // default: a one-day rental
  });
  const [error, setError] = useState("");

  // Dates can still be typed by hand: checked before searching
  const submit = () => {
    if (query.start < today()) return setError(t.home.search.pastDate);
    if (query.end <= query.start) return setError(t.home.search.endBeforeStart);
    setError("");
    onSearch(query);
  };

  const update = <K extends keyof SearchQuery>(key: K, value: SearchQuery[K]) => {
    setError("");
    setQuery((q) => ({ ...q, [key]: value }));
  };

  return (
    <div className="relative z-20 mx-auto -mt-11 max-w-208 px-4">
      <div className="grid gap-1.5 rounded-lg bg-white p-2 shadow-[0_8px_32px_rgba(23,26,32,.12)] sm:grid-cols-2 lg:grid-cols-4">
        <div className={fieldWrap}>
          <span className={fieldLabel}>{t.home.search.location}</span>
          {/* Cities where companies have published vehicles ("" = every location) */}
          <CityAutocomplete
            value={query.city}
            onChange={(city) => update("city", city)}
            ariaLabel={t.home.search.location}
            inputClassName={fieldInput}
          />
        </div>

        <label className={fieldWrap}>
          <span className={fieldLabel}>{t.home.search.start}</span>
          <input
            type="date"
            value={query.start}
            min={today()}
            onChange={(e) => {
              setError("");
              const start = e.target.value;
              // The end stays at least one day after the start
              setQuery((q) => ({ ...q, start, end: start >= q.end ? addDaysIso(start, 1) : q.end }));
            }}
            className={fieldInput}
          />
        </label>

        <label className={fieldWrap}>
          <span className={fieldLabel}>{t.home.search.end}</span>
          <input
            type="date"
            value={query.end}
            min={addDaysIso(query.start, 1)}
            onChange={(e) => update("end", e.target.value)}
            className={fieldInput}
          />
        </label>

        <button
          type="button"
          onClick={submit}
          aria-label={t.home.search.submitAria}
          className="min-h-12 cursor-pointer rounded border-0 bg-brand px-6 text-[13px] font-semibold text-white transition-colors hover:bg-brand-dark sm:col-span-2 lg:col-span-1"
        >
          {t.home.search.submit}
        </button>
      </div>
      {error && (
        <p role="alert" className="m-0 mt-2 rounded bg-white px-3 py-2 text-center text-[13px] text-tone-red-fg shadow-sm">
          {error}
        </p>
      )}
    </div>
  );
}
